import React, { useEffect, useState, useCallback } from 'react';
import { WhatsappLogo, Lightning, Plus, Trash, ArrowsClockwise, Users, PaperPlaneTilt, Eye, ChartBar, CheckCircle, Warning } from '@phosphor-icons/react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';

const STATUS_META = {
  draft: { label: 'Borrador', color: 'bg-slate-500/20 text-slate-300' },
  sending: { label: 'Enviando', color: 'bg-blue-500/20 text-blue-300' },
  completed: { label: 'Completada', color: 'bg-emerald-500/20 text-emerald-300' },
};

const MSG_STATUS_LABEL = {
  pending: { label: 'Pendiente', color: 'text-slate-400' },
  sent: { label: 'Enviado', color: 'text-blue-300' },
  delivered: { label: 'Entregado', color: 'text-cyan-300' },
  read: { label: 'Leído', color: 'text-violet-300' },
  failed: { label: 'Falló', color: 'text-red-300' },
  replied: { label: 'Respondió', color: 'text-emerald-300' },
  opt_out: { label: 'Opt-out', color: 'text-orange-300' },
};

const fmtPct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

const WaCampaigns = () => {
  const { selectedBusiness } = useOutletContext();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const load = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    try {
      setLoading(true);
      const res = await api.get(`/wa-campaigns/campaigns?business_id=${selectedBusiness.id}`);
      setCampaigns(res.data?.campaigns || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando campañas');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness?.id]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="page-wa-campaigns">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shrink-0">
            <WhatsappLogo size={26} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-[var(--text)] heading-font">Campañas WhatsApp</h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">
              Envíos masivos con plantillas aprobadas por Meta · Negocio: <b>{selectedBusiness?.name || '—'}</b>
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          disabled={!selectedBusiness?.id}
          className="px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white rounded-[10px] font-bold text-sm flex items-center gap-2 disabled:opacity-50"
          data-testid="btn-new-campaign"
        >
          <Plus size={16} weight="bold" /> Nueva campaña
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[var(--text-muted)]">Cargando…</p>
      ) : !selectedBusiness?.id ? (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-[12px] p-6 text-center">
          <Warning size={32} weight="duotone" className="mx-auto text-amber-400 mb-2" />
          <p className="text-sm text-amber-200">Selecciona un negocio en el menú superior para empezar.</p>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-10 text-center">
          <Lightning size={48} weight="duotone" className="mx-auto text-emerald-400 mb-3" />
          <p className="text-base font-bold text-[var(--text)]">Aún no hay campañas</p>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Crea tu primera campaña con una plantilla aprobada por Meta.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {campaigns.map((c) => {
            const m = c.metrics || {};
            const total = c.audience_count || 0;
            const sent = (m.sent || 0) + (m.delivered || 0) + (m.read || 0) + (m.replied || 0);
            return (
              <button
                key={c.id}
                onClick={() => setSelectedCampaign(c.id)}
                className="text-left bg-[var(--surface)] border border-[var(--border)] hover:border-emerald-500/40 rounded-[12px] p-4 transition-colors"
                data-testid={`campaign-card-${c.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_META[c.status]?.color || 'bg-slate-500/20'}`}>
                    {STATUS_META[c.status]?.label || c.status}
                  </span>
                  <span className="text-[10px] text-[var(--text-faint)]">{new Date(c.created_at).toLocaleDateString('es-GT')}</span>
                </div>
                <h3 className="text-sm font-bold text-[var(--text)] mb-1">{c.name}</h3>
                <p className="text-[11px] text-[var(--text-muted)] mb-2 truncate">📄 {c.template_name}</p>
                <div className="grid grid-cols-4 gap-1.5 text-center mt-3 text-[10px]">
                  <div>
                    <p className="font-black text-[var(--text)]">{total}</p>
                    <p className="text-[var(--text-faint)]">Total</p>
                  </div>
                  <div>
                    <p className="font-black text-blue-400">{sent}</p>
                    <p className="text-[var(--text-faint)]">Enviado</p>
                  </div>
                  <div>
                    <p className="font-black text-emerald-400">{m.replied || 0}</p>
                    <p className="text-[var(--text-faint)]">Resp.</p>
                  </div>
                  <div>
                    <p className="font-black text-red-400">{m.failed || 0}</p>
                    <p className="text-[var(--text-faint)]">Falló</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateCampaignModal
          businessId={selectedBusiness?.id}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {selectedCampaign && (
        <CampaignDetailModal
          campaignId={selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
          onRefresh={load}
        />
      )}
    </div>
  );
};


// ─────────────────────────── Create Modal ───────────────────────────
const CreateCampaignModal = ({ businessId, onClose, onCreated }) => {
  const [step, setStep] = useState(1); // 1 template · 2 audience · 3 review
  const [templates, setTemplates] = useState([]);
  const [loadingTpl, setLoadingTpl] = useState(true);
  const [selectedTpl, setSelectedTpl] = useState(null);
  const [varMap, setVarMap] = useState({});
  const [name, setName] = useState('');
  const [audience, setAudience] = useState({
    source: 'clientes_vendedores',
    min_total: null, min_sales: null,
    days_inactive_min: null, days_inactive_max: null,
    branches: null, manual_phones: null,
  });
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchTpl = async (refresh = false) => {
      try {
        setLoadingTpl(true);
        const res = await api.get(`/wa-campaigns/templates?business_id=${businessId}${refresh ? '&refresh=true' : ''}`);
        setTemplates(res.data?.templates || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'No se pudieron cargar plantillas');
      } finally {
        setLoadingTpl(false);
      }
    };
    fetchTpl(false);
  }, [businessId]);

  const refreshTemplates = async () => {
    try {
      setLoadingTpl(true);
      const res = await api.get(`/wa-campaigns/templates?business_id=${businessId}&refresh=true`);
      setTemplates(res.data?.templates || []);
      toast.success('Plantillas sincronizadas desde Meta');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error sincronizando');
    } finally {
      setLoadingTpl(false);
    }
  };

  const previewAudience = async () => {
    try {
      const res = await api.post('/wa-campaigns/audience/preview', audience);
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error en preview');
    }
  };

  const submit = async () => {
    if (!name || !selectedTpl) { toast.error('Falta nombre y plantilla'); return; }
    try {
      setSubmitting(true);
      const res = await api.post('/wa-campaigns/campaigns', {
        business_id: businessId,
        name,
        template_name: selectedTpl.name,
        template_language: selectedTpl.language,
        template_variables_map: varMap,
        audience,
      });
      toast.success(`Campaña creada con ${res.data.audience_count} destinatarios`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error creando campaña');
    } finally {
      setSubmitting(false);
    }
  };

  const tplApproved = templates.filter((t) => (t.status || '').toUpperCase() === 'APPROVED');

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="create-campaign-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black text-[var(--text)]">Nueva campaña WhatsApp · paso {step}/3</h2>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text)]">✕</button>
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-[var(--text-muted)]">Plantillas aprobadas por Meta</p>
              <button
                onClick={refreshTemplates}
                className="text-[11px] flex items-center gap-1 text-emerald-400 hover:underline"
                data-testid="btn-refresh-templates"
              >
                <ArrowsClockwise size={12} /> Sincronizar
              </button>
            </div>
            {loadingTpl ? (
              <p className="text-sm">Cargando plantillas…</p>
            ) : tplApproved.length === 0 ? (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3 text-xs text-amber-200">
                No hay plantillas aprobadas. Créalas en WhatsApp Manager y luego sincroniza.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
                {tplApproved.map((t) => (
                  <button
                    key={`${t.name}_${t.language}`}
                    onClick={() => {
                      setSelectedTpl(t);
                      const m = {};
                      for (let i = 1; i <= (t.variable_count || 0); i++) m[String(i)] = '';
                      setVarMap(m);
                    }}
                    className={`w-full text-left p-3 rounded-md border ${selectedTpl?.name === t.name ? 'border-emerald-500 bg-emerald-500/10' : 'border-[var(--border)]'} hover:border-emerald-500/60`}
                    data-testid={`template-${t.name}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-[var(--text)]">{t.name}</span>
                      <span className="text-[10px] uppercase font-bold text-emerald-400 bg-emerald-500/20 px-1.5 py-0.5 rounded">{t.category}</span>
                    </div>
                    <p className="text-[11px] text-[var(--text-muted)] mt-1">{t.language} · {t.variable_count} variables</p>
                    <p className="text-xs text-[var(--text-muted)] mt-1.5 whitespace-pre-wrap">{t.body_text}</p>
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-3 border-t border-[var(--border)]">
              <button onClick={onClose} className="px-4 py-2 text-sm font-bold text-[var(--text-muted)]">Cancelar</button>
              <button
                onClick={() => setStep(2)}
                disabled={!selectedTpl}
                className="px-4 py-2 text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-md disabled:opacity-40"
                data-testid="btn-step-1-next"
              >Siguiente →</button>
            </div>
          </div>
        )}

        {step === 2 && selectedTpl && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] uppercase">Nombre de campaña</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Promo Yale Marzo 2026"
                className="w-full mt-1 px-3 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded-md text-sm"
                data-testid="campaign-name-input"
              />
            </div>

            {selectedTpl.variable_count > 0 && (
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] uppercase">Variables del template</label>
                <p className="text-[11px] text-[var(--text-faint)] mb-2">
                  Usa <code>{'{{customer_name}}'}</code> para nombre del cliente, <code>{'{{first_name}}'}</code> para primer nombre, o escribe un texto literal.
                </p>
                <div className="space-y-1.5">
                  {Object.keys(varMap).map((k) => (
                    <div key={k} className="flex items-center gap-2">
                      <span className="text-xs font-bold text-emerald-400 w-12">{`{{${k}}}`}</span>
                      <input
                        value={varMap[k]}
                        onChange={(e) => setVarMap({ ...varMap, [k]: e.target.value })}
                        placeholder={k === '1' ? '{{first_name}}' : 'texto o {{campo}}'}
                        className="flex-1 px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-xs"
                        data-testid={`var-input-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] uppercase">Audiencia</label>
              <select
                value={audience.source}
                onChange={(e) => setAudience({ ...audience, source: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded-md text-sm"
                data-testid="audience-source"
              >
                <option value="clientes_vendedores">Clientes Vendedores (Plan Trabajo)</option>
                <option value="customers">Clientes RRSS (del Inbox)</option>
                <option value="manual">Lista manual (números pegados)</option>
              </select>
            </div>

            {audience.source !== 'manual' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase text-[var(--text-muted)]">Compras mínimas</label>
                  <input type="number" min="0"
                    onChange={(e) => setAudience({ ...audience, min_sales: e.target.value ? Number(e.target.value) : null })}
                    placeholder="ej. 1"
                    className="w-full mt-0.5 px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-xs"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-[var(--text-muted)]">Monto histórico mínimo (Q)</label>
                  <input type="number" min="0"
                    onChange={(e) => setAudience({ ...audience, min_total: e.target.value ? Number(e.target.value) : null })}
                    placeholder="ej. 5000"
                    className="w-full mt-0.5 px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-xs"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-[var(--text-muted)]">Inactivo mínimo (días)</label>
                  <input type="number" min="0"
                    onChange={(e) => setAudience({ ...audience, days_inactive_min: e.target.value ? Number(e.target.value) : null })}
                    placeholder="recuperar dormidos: ej. 60"
                    className="w-full mt-0.5 px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-xs"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-[var(--text-muted)]">Inactivo máximo (días)</label>
                  <input type="number" min="0"
                    onChange={(e) => setAudience({ ...audience, days_inactive_max: e.target.value ? Number(e.target.value) : null })}
                    placeholder="máx 365"
                    className="w-full mt-0.5 px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-xs"
                  />
                </div>
              </div>
            )}
            {audience.source === 'manual' && (
              <textarea
                rows={5}
                placeholder="Un número por línea (con o sin código país)"
                onChange={(e) => setAudience({ ...audience, manual_phones: e.target.value.split(/\n+/).map(s => s.trim()).filter(Boolean) })}
                className="w-full px-3 py-2 bg-[var(--surface-muted)] border border-[var(--border)] rounded-md text-xs font-mono"
                data-testid="manual-phones-input"
              />
            )}

            <button
              onClick={previewAudience}
              className="w-full px-3 py-2 border border-emerald-500/40 text-emerald-300 rounded-md text-sm font-bold flex items-center justify-center gap-2"
              data-testid="btn-preview-audience"
            >
              <Eye size={14} weight="bold" /> Previsualizar audiencia
            </button>

            {preview && (
              <div className="bg-[var(--surface-muted)] border border-[var(--border)] rounded-md p-3">
                <p className="text-sm font-bold text-[var(--text)] flex items-center gap-2">
                  <Users size={14} weight="bold" /> {preview.count} destinatarios elegibles
                </p>
                {preview.sample?.length > 0 && (
                  <p className="text-[10px] text-[var(--text-faint)] mt-1">
                    Muestra: {preview.sample.slice(0, 3).map(p => p.customer_name || p.phone).join(', ')}…
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-between gap-2 pt-3 border-t border-[var(--border)]">
              <button onClick={() => setStep(1)} className="px-4 py-2 text-sm font-bold text-[var(--text-muted)]">← Atrás</button>
              <button
                onClick={() => setStep(3)}
                disabled={!name || (preview?.count || 0) === 0}
                className="px-4 py-2 text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-md disabled:opacity-40"
                data-testid="btn-step-2-next"
              >Siguiente →</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div className="bg-[var(--surface-muted)] border border-[var(--border)] rounded-md p-3 space-y-1.5">
              <p className="text-xs"><b>Nombre:</b> {name}</p>
              <p className="text-xs"><b>Plantilla:</b> {selectedTpl.name} ({selectedTpl.language})</p>
              <p className="text-xs"><b>Variables:</b> {Object.entries(varMap).map(([k, v]) => `{{${k}}}=${v}`).join(' · ') || 'ninguna'}</p>
              <p className="text-xs"><b>Audiencia:</b> {preview?.count || 0} destinatarios</p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3">
              <p className="text-xs text-amber-200 font-bold flex items-center gap-1.5">
                <Warning size={14} weight="fill" /> Importante
              </p>
              <p className="text-[11px] text-amber-100/90 mt-1">
                Meta cobra por cada conversación iniciada (~$0.04-0.08 USD/cliente). Tu número WhatsApp Business debe tener un tier de mensajería suficiente. Esta acción NO se puede deshacer.
              </p>
            </div>
            <div className="flex justify-between gap-2 pt-3 border-t border-[var(--border)]">
              <button onClick={() => setStep(2)} className="px-4 py-2 text-sm font-bold text-[var(--text-muted)]">← Atrás</button>
              <button
                onClick={submit}
                disabled={submitting}
                className="px-5 py-2 text-sm font-bold bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 text-white rounded-md disabled:opacity-50 flex items-center gap-2"
                data-testid="btn-create-campaign"
              >
                <CheckCircle size={14} weight="fill" />
                {submitting ? 'Creando…' : 'Crear campaña'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// ─────────────────────────── Detail Modal ───────────────────────────
const CampaignDetailModal = ({ campaignId, onClose, onRefresh }) => {
  const [campaign, setCampaign] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/wa-campaigns/campaigns/${campaignId}`);
      setCampaign(res.data);
      const m = await api.get(`/wa-campaigns/campaigns/${campaignId}/messages?limit=200`);
      setMessages(m.data?.messages || []);
    } catch (e) {
      toast.error('Error cargando campaña');
    }
  }, [campaignId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const triggerSend = async () => {
    if (!window.confirm(`¿Enviar ${campaign.audience_count} mensajes WhatsApp ahora? Esto consume créditos de Meta.`)) return;
    try {
      setSending(true);
      await api.post(`/wa-campaigns/campaigns/${campaignId}/send`);
      toast.success('Campaña encolada — los envíos comienzan ya');
      await load();
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setSending(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('¿Eliminar esta campaña?')) return;
    try {
      await api.delete(`/wa-campaigns/campaigns/${campaignId}`);
      toast.success('Campaña eliminada');
      onClose();
      onRefresh?.();
    } catch (e) {
      toast.error('No se pudo eliminar');
    }
  };

  if (!campaign) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
        <p className="text-white">Cargando…</p>
      </div>
    );
  }

  const m = campaign.metrics || {};
  const total = campaign.audience_count || 1;
  const filtered = filter === 'all' ? messages : messages.filter((x) => x.status === filter);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] max-w-3xl w-full max-h-[90vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="campaign-detail-modal"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-lg font-black text-[var(--text)]">{campaign.name}</h2>
            <p className="text-xs text-[var(--text-muted)]">📄 {campaign.template_name} · {campaign.audience_count} destinatarios</p>
          </div>
          <button onClick={onClose} className="text-[var(--text-muted)]">✕</button>
        </div>

        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
          {['pending', 'sent', 'delivered', 'read', 'replied', 'failed'].map((k) => (
            <div key={k} className="bg-[var(--surface-muted)] border border-[var(--border)] rounded-md p-2 text-center">
              <p className="text-[9px] uppercase font-bold text-[var(--text-muted)]">{MSG_STATUS_LABEL[k]?.label}</p>
              <p className={`text-lg font-black ${MSG_STATUS_LABEL[k]?.color || 'text-[var(--text)]'}`}>{m[k] || 0}</p>
              <p className="text-[9px] text-[var(--text-faint)]">{fmtPct(m[k] || 0, total)}%</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          {campaign.status === 'draft' && (
            <button
              onClick={triggerSend}
              disabled={sending}
              className="px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-md text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
              data-testid="btn-send-campaign"
            >
              <PaperPlaneTilt size={12} weight="fill" /> Enviar ahora
            </button>
          )}
          <button
            onClick={remove}
            className="px-3 py-1.5 border border-red-500/30 text-red-300 rounded-md text-xs font-bold flex items-center gap-1.5"
            data-testid="btn-delete-campaign"
          >
            <Trash size={12} /> Eliminar
          </button>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded-md text-xs ml-auto"
          >
            <option value="all">Todos los mensajes</option>
            <option value="failed">Solo fallidos</option>
            <option value="replied">Solo respondieron</option>
            <option value="opt_out">Opt-out (STOP)</option>
          </select>
        </div>

        <div className="bg-[var(--surface-muted)] rounded-md max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--surface)]">
              <tr className="text-[10px] uppercase text-[var(--text-muted)]">
                <th className="text-left py-1.5 px-2">Teléfono</th>
                <th className="text-left py-1.5 px-2">Cliente</th>
                <th className="text-left py-1.5 px-2">Estado</th>
                <th className="text-right py-1.5 px-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 100).map((msg) => (
                <tr key={msg.id} className="border-b border-[var(--border)]/40">
                  <td className="py-1 px-2 font-mono">{msg.phone}</td>
                  <td className="py-1 px-2">{msg.customer_name || '—'}</td>
                  <td className={`py-1 px-2 font-bold ${MSG_STATUS_LABEL[msg.status]?.color || ''}`}>
                    {MSG_STATUS_LABEL[msg.status]?.label || msg.status}
                  </td>
                  <td className="py-1 px-2 text-right text-[10px] text-red-300 truncate max-w-[180px]">
                    {msg.error?.message || msg.error?.error_data?.details || ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WaCampaigns;
